import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import {
  Plus, Pencil, Trash2, ArrowRightLeft, UserCheck, UserPlus,
  MessageSquare, Paperclip, Link2, Unlink, CheckCircle2, RotateCcw, AlertCircle,
} from 'lucide-react';
import { taskApi } from '../api/taskApi';
import { useAttorneys } from '@/features/matters/hooks/useAttorneys';
import { notificationApi } from '@/features/notifications/api/notificationApi';
import { cn } from '@/lib/utils';
import type {
  Task,
  TaskComment,
  TaskAuditLog,
  TaskStatus,
  TaskPriority,
  SubtaskProgress,
  UpdateTaskDto,
  CreateTaskDto,
} from '../types/task.types';
import type { TaskReminder, ReminderMethod, ReminderHealthStatus } from '@/features/notifications/types/notification.types';
import { TaskPlaybookPanel } from '@/features/playbooks/components/TaskPlaybookPanel';

interface TaskDetailModalProps {
  isOpen: boolean;
  taskId: string | null;
  onClose: () => void;
  onUpdated?: () => void;
  /** When taskId is null and initialData is provided, modal opens in create mode */
  initialData?: Partial<CreateTaskDto>;
  initialTab?: 'details' | 'reminders' | 'subtasks' | 'comments' | 'activity';
  theme?: 'default' | 'edgemail';
}

const STATUS_OPTIONS: { value: TaskStatus; label: string }[] = [
  { value: 'todo', label: 'To Do' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'review', label: 'In Review' },
  { value: 'done', label: 'Done' },
  { value: 'archived', label: 'Archived' },
];

const PRIORITY_OPTIONS: { value: TaskPriority; label: string }[] = [
  { value: 'urgent', label: 'Urgent' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

export const TaskDetailModal: React.FC<TaskDetailModalProps> = ({
  isOpen,
  taskId,
  onClose,
  onUpdated,
  initialData,
  initialTab = 'details',
  theme = 'default',
}) => {
  const isCreateMode = !taskId && !!initialData;
  const [task, setTask] = useState<Task | null>(null);
  const [comments, setComments] = useState<TaskComment[]>([]);
  const [auditLog, setAuditLog] = useState<TaskAuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'details' | 'reminders' | 'subtasks' | 'comments' | 'activity'>('details');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showReassign, setShowReassign] = useState(false);
  const [reassigning, setReassigning] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [editForm, setEditForm] = useState<UpdateTaskDto>({});
  const [reminders, setReminders] = useState<TaskReminder[]>([]);
  const [remindersLoading, setRemindersLoading] = useState(false);
  const [reminderHealth, setReminderHealth] = useState<ReminderHealthStatus | null>(null);
  const [reminderHealthLoading, setReminderHealthLoading] = useState(false);
  const [showHealthDetails, setShowHealthDetails] = useState(false);
  const [showAddReminder, setShowAddReminder] = useState(false);
  const [newReminderAt, setNewReminderAt] = useState('');
  const [newReminderMethod, setNewReminderMethod] = useState<ReminderMethod>('in_app');
  const [savingReminder, setSavingReminder] = useState(false);
  const [editingReminderId, setEditingReminderId] = useState<string | null>(null);
  const [editingReminderAt, setEditingReminderAt] = useState('');
  const [reminderAction, setReminderAction] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  // Subtasks
  const [subtasks, setSubtasks] = useState<Task[]>([]);
  const [subtaskProgress, setSubtaskProgress] = useState<SubtaskProgress>({ total: 0, completed: 0 });
  const [showAddSubtask, setShowAddSubtask] = useState(false);
  const [newSubtask, setNewSubtask] = useState<Partial<CreateTaskDto>>({ title: '', assignee_id: '', due_date: '' });
  const [notifyOnCreate, setNotifyOnCreate] = useState(false);
  const [submittingSubtask, setSubmittingSubtask] = useState(false);
  const [editingSubtaskId, setEditingSubtaskId] = useState<string | null>(null);
  const [editSubtaskForm, setEditSubtaskForm] = useState<{ title: string; assignee_id: string; due_date: string }>({ title: '', assignee_id: '', due_date: '' });
  const [savingSubtask, setSavingSubtask] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [subtaskModalTaskId, setSubtaskModalTaskId] = useState<string | null>(null);
  const [subtaskModalInitialTab, setSubtaskModalInitialTab] = useState<'details' | 'reminders' | 'subtasks' | 'comments' | 'activity'>('details');
  const { attorneys } = useAttorneys();
  const isEdgeMail = theme === 'edgemail';

  const modalShellClass = cn(
    'relative rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden',
    isEdgeMail
      ? 'bg-[var(--em-surface-card)] border border-[var(--em-border-primary)] em-smooth'
      : 'bg-white dark:bg-gray-800',
  );
  const headerClass = cn(
    'flex items-center justify-between px-6 py-4 border-b',
    isEdgeMail
      ? 'bg-[var(--em-surface-toolbar)] border-[var(--em-border-primary)]'
      : 'border-gray-200 dark:border-gray-700',
  );
  const titleClass = isEdgeMail ? 'text-[var(--em-text-primary)]' : 'text-gray-900 dark:text-white';
  const closeButtonClass = cn(
    'p-1 rounded-lg transition-colors',
    isEdgeMail
      ? 'text-[var(--em-text-tertiary)] hover:text-[var(--em-text-primary)] hover:bg-[var(--em-state-hover)]'
      : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500',
  );
  const tabBarClass = cn('flex border-b px-6', isEdgeMail ? 'border-[var(--em-border-primary)]' : 'border-gray-200 dark:border-gray-700');
  const tabActiveClass = isEdgeMail ? 'border-[var(--em-accent-primary)] text-[var(--em-accent-primary)]' : 'border-lex-accent text-lex-accent';
  const tabInactiveClass = isEdgeMail
    ? 'border-transparent text-[var(--em-text-tertiary)] hover:text-[var(--em-text-primary)] hover:bg-[var(--em-state-hover)]'
    : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300';
  const textPrimary = isEdgeMail ? 'text-[var(--em-text-primary)]' : 'text-gray-900 dark:text-gray-100';
  const textSecondary = isEdgeMail ? 'text-[var(--em-text-secondary)]' : 'text-gray-700 dark:text-gray-300';
  const textTertiary = isEdgeMail ? 'text-[var(--em-text-tertiary)]' : 'text-gray-500 dark:text-gray-400';
  const textMuted = isEdgeMail ? 'text-[var(--em-text-muted)]' : 'text-gray-400';
  const backdropClass = isEdgeMail ? 'bg-[var(--em-overlay-backdrop)]' : 'bg-black/50';
  const inputClass = isEdgeMail
    ? 'bg-[var(--em-surface-toolbar)] border-[var(--em-border-secondary)] text-[var(--em-text-primary)] placeholder-[var(--em-text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--em-state-focus)] focus:border-[var(--em-accent-primary)]'
    : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white';
  const selectClass = isEdgeMail
    ? 'bg-[var(--em-surface-toolbar)] border-[var(--em-border-secondary)] text-[var(--em-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--em-state-focus)] focus:border-[var(--em-accent-primary)]'
    : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white';
  const primaryButtonClass = isEdgeMail
    ? 'bg-[var(--em-btn-primary-bg)] text-[var(--em-btn-primary-text)] hover:bg-[var(--em-btn-primary-bg-hover)]'
    : 'bg-lex-accent text-lex-accent-contrast hover:bg-lex-accent-hover';
  const secondaryButtonClass = isEdgeMail
    ? 'bg-[var(--em-btn-secondary-bg)] text-[var(--em-btn-secondary-text)] hover:bg-[var(--em-btn-secondary-bg-hover)]'
    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600';
  const accentTextClass = isEdgeMail ? 'text-[var(--em-accent-primary)]' : 'text-lex-accent';
  const accentTextHoverClass = isEdgeMail ? 'hover:text-[var(--em-accent-primary-hover)]' : 'hover:text-lex-accent-hover';
  const accentChipClass = isEdgeMail
    ? 'bg-[var(--em-accent-primary-light)] text-[var(--em-accent-primary)]'
    : 'bg-lex-accent-soft text-lex-accent';

  const fetchTask = useCallback(async () => {
    if (!taskId) return;
    try {
      setLoading(true);
      const result = await taskApi.getById(taskId);
      const data = result as any;
      setTask(data.task || null);
    } catch {
      setTask(null);
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  const fetchComments = useCallback(async () => {
    if (!taskId) return;
    try {
      const result = await taskApi.getComments(taskId);
      const data = result as any;
      setComments(data.comments || []);
    } catch { /* ignore */ }
  }, [taskId]);

  const fetchAuditLog = useCallback(async () => {
    if (!taskId) return;
    try {
      const result = await taskApi.getAuditLog(taskId);
      const data = result as any;
      setAuditLog(data.logs || []);
    } catch { /* ignore */ }
  }, [taskId]);

  const fetchSubtasks = useCallback(async () => {
    if (!taskId) return;
    try {
      const result = await taskApi.getSubtasks(taskId);
      const data = result as any;
      setSubtasks(data.subtasks || []);
      setSubtaskProgress(data.progress || { total: 0, completed: 0 });
    } catch { /* ignore */ }
  }, [taskId]);

  const fetchReminders = useCallback(async () => {
    if (!taskId) return;
    try {
      setRemindersLoading(true);
      const result = await notificationApi.getReminders(taskId);
      const data = result as any;
      const activeReminders = (data.reminders || []).filter((reminder: TaskReminder) => reminder.status !== 'dismissed');
      setReminders(activeReminders);
    } catch {
      setReminders([]);
    } finally {
      setRemindersLoading(false);
    }
  }, [taskId]);

  const fetchReminderHealth = useCallback(async () => {
    try {
      setReminderHealthLoading(true);
      const result = await notificationApi.getReminderHealth();
      setReminderHealth(result as any);
    } catch {
      setReminderHealth(null);
    } finally {
      setReminderHealthLoading(false);
    }
  }, []);

  // Reset ALL state when modal opens with a (new) task — prevents stale data flash
  useEffect(() => {
    if (isOpen && taskId) {
      // Clear previous task data immediately so old content doesn't flash
      setTask(null);
      setComments([]);
      setAuditLog([]);
      setSubtasks([]);
      setSubtaskProgress({ total: 0, completed: 0 });
      setReminders([]);
      setReminderHealth(null);
      setLoading(true);

      // Reset UI state
      setActiveTab(initialTab);
      setEditing(false);
      setNewComment('');
      setShowAddSubtask(false);
      setShowHealthDetails(false);
      setShowReassign(false);
      setShowAddReminder(false);
      setNewSubtask({ title: '', assignee_id: '', due_date: '' });
      setReminderAction(null);
      setEditingSubtaskId(null);
      setEditingReminderId(null);
      setSubtaskModalTaskId(null);

      // Fetch fresh data
      fetchTask();
      fetchComments();
      fetchAuditLog();
      fetchSubtasks();
      fetchReminders();
    }
  }, [isOpen, taskId, initialTab, fetchTask, fetchComments, fetchAuditLog, fetchSubtasks, fetchReminders]);

  useEffect(() => {
    if (isOpen && activeTab === 'reminders') {
      fetchReminderHealth();
    }
  }, [isOpen, activeTab, fetchReminderHealth]);

  const renderHealthBadge = (label: string, status: string, detail?: string) => {
    const isHealthy = status === 'healthy';
    const isDegraded = status === 'degraded';
    const badgeClass = isHealthy
      ? 'bg-green-100 text-green-700'
      : isDegraded
        ? 'bg-amber-100 text-amber-700'
        : 'bg-red-100 text-red-700';
    return (
      <div className="flex items-center gap-2 text-xs">
        <span className={`px-2 py-0.5 rounded-full ${badgeClass}`}>{label}</span>
        <span className={textTertiary}>{detail || status}</span>
      </div>
    );
  };

  const renderDeliveryDetails = (reminder: TaskReminder) => {
    const metadata = (reminder.delivery_metadata || {}) as any;
    const entries: Array<{ label: string; value: string }> = [];

    if (reminder.sent_time) {
      entries.push({ label: 'Sent', value: formatDateTime(reminder.sent_time) });
    }

    if (metadata.in_app?.status) {
      entries.push({ label: 'In-app', value: metadata.in_app.status });
    }
    if (metadata.push?.status) {
      entries.push({ label: 'Push', value: metadata.push.status });
    }
    if (metadata.email?.status) {
      entries.push({ label: 'Email', value: metadata.email.status });
      if (metadata.email.error) {
        entries.push({ label: 'Email Error', value: metadata.email.error });
      }
    }
    if (metadata.sms?.status) {
      entries.push({ label: 'SMS', value: metadata.sms.status });
      if (metadata.sms.reason) {
        entries.push({ label: 'SMS Reason', value: metadata.sms.reason });
      }
      if (metadata.sms.error) {
        entries.push({ label: 'SMS Error', value: metadata.sms.error });
      }
    }
    if (reminder.failure_reason) {
      entries.push({ label: 'Failure', value: reminder.failure_reason });
    }

    if (entries.length === 0) {
      return <div className={cn('text-xs mt-2', textTertiary)}>Delivery: No delivery metadata yet.</div>;
    }

    return (
      <div className={cn('text-xs mt-2 space-y-1', textTertiary)}>
        {entries.map((entry, idx) => (
          <div key={`${reminder.id}-${idx}`}>
            <span className={textMuted}>{entry.label}:</span>
            <span className={cn('ml-2', textSecondary)}>{entry.value}</span>
          </div>
        ))}
      </div>
    );
  };

  // Create mode: pre-populate edit form from initialData
  useEffect(() => {
    if (isOpen && isCreateMode && initialData) {
      setTask(null);
      setLoading(false);
      setEditing(true);
      setActiveTab('details');
      setEditForm({
        title: initialData.title || '',
        description: initialData.description || '',
        priority: initialData.priority || 'medium',
        due_date: initialData.due_date ? initialData.due_date.substring(0, 10) : '',
        tags: initialData.tags || [],
        estimated_hours: initialData.estimated_hours,
      });
    }
  }, [isOpen, isCreateMode, initialData]);

  const handleCreate = async () => {
    if (!isCreateMode || !editForm.title) return;
    try {
      setSaving(true);
      await taskApi.create({
        ...initialData,
        title: editForm.title as string,
        description: editForm.description as string,
        priority: editForm.priority as TaskPriority,
        due_date: editForm.due_date || undefined,
        tags: editForm.tags,
        estimated_hours: editForm.estimated_hours,
      } as CreateTaskDto);
      onUpdated?.();
      onClose();
    } catch (err) {
      console.error('Failed to create task:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleNotifyAssignee = async () => {
    if (!taskId || notifying) return;
    try {
      setNotifying(true);
      await taskApi.notifyAssignee(taskId);
      await fetchTask();
    } catch (err) {
      console.error('Failed to notify assignee:', err);
    } finally {
      setNotifying(false);
    }
  };

  const handleReassign = async (newAssigneeId: string) => {
    if (!taskId || reassigning) return;
    try {
      setReassigning(true);
      await taskApi.update(taskId, { assignee_id: newAssigneeId });
      setShowReassign(false);
      await fetchTask();
      await fetchAuditLog();
      onUpdated?.();
    } catch (err) {
      console.error('Failed to reassign task:', err);
    } finally {
      setReassigning(false);
    }
  };

  const handleSave = async () => {
    if (!taskId) return;
    try {
      setSaving(true);
      // Capture any unsaved tag text from the input before saving
      const tagInput = document.getElementById('tag-input-field') as HTMLInputElement | null;
      let finalForm = editForm;
      if (tagInput && tagInput.value.trim()) {
        const pendingTag = tagInput.value.trim();
        if (!(editForm.tags || []).includes(pendingTag)) {
          finalForm = { ...editForm, tags: [...(editForm.tags || []), pendingTag] };
        }
        tagInput.value = '';
      }
      await taskApi.update(taskId, finalForm);
      setEditing(false);
      await fetchTask();
      onUpdated?.();
    } catch (err) { console.error('Failed to save task:', err); }
    finally { setSaving(false); }
  };

  const handleStatusChange = async (status: TaskStatus) => {
    if (!taskId) return;
    try {
      await taskApi.updateStatus(taskId, status);
      await fetchTask();
      onUpdated?.();
    } catch { /* ignore */ }
  };

  const handleAddComment = async () => {
    if (!taskId || !newComment.trim()) return;
    try {
      await taskApi.createComment(taskId, { content: newComment });
      setNewComment('');
      await fetchComments();
    } catch { /* ignore */ }
  };

  const startEditing = () => {
    if (!task) return;
    setEditForm({
      title: task.title,
      description: task.description,
      priority: task.priority,
      due_date: task.due_date ? task.due_date.substring(0, 10) : '',
      tags: task.tags,
      estimated_hours: task.estimated_hours,
    });
    setEditing(true);
  };

  const handleCreateSubtask = async () => {
    if (!taskId || !newSubtask.title?.trim()) return;
    try {
      setSubmittingSubtask(true);
      const result = await taskApi.createSubtask(taskId, newSubtask as CreateTaskDto);
      const createdSubtask = result.data?.task;
      // If notify checkbox is checked and an assignee was selected, send notification
      if (notifyOnCreate && newSubtask.assignee_id && createdSubtask?.id) {
        try {
          await taskApi.notifyAssignee(createdSubtask.id);
        } catch {
          console.error('Failed to send assignment notification for subtask');
        }
      }
      setShowAddSubtask(false);
      setNewSubtask({ title: '', assignee_id: '', due_date: '' });
      setNotifyOnCreate(false);
      await fetchSubtasks();
      onUpdated?.();
    } catch { /* ignore */ }
    finally { setSubmittingSubtask(false); }
  };

  const handleSubtaskStatusToggle = async (subtask: Task) => {
    try {
      const newStatus = subtask.status === 'done' ? 'todo' : 'done';
      await taskApi.updateStatus(subtask.id, newStatus);
      await fetchSubtasks();
      onUpdated?.();
    } catch { /* ignore */ }
  };

  const handleDeleteSubtask = async (subtaskId: string) => {
    try {
      await taskApi.delete(subtaskId);
      await fetchSubtasks();
      onUpdated?.();
    } catch { /* ignore */ }
  };

  const handlePromoteSubtask = async (subtaskId: string) => {
    try {
      await taskApi.promoteSubtask(subtaskId);
      await fetchSubtasks();
      onUpdated?.();
    } catch { /* ignore */ }
  };

  const startEditingSubtask = (st: Task) => {
    setEditingSubtaskId(st.id);
    setEditSubtaskForm({
      title: st.title,
      assignee_id: st.assignee_id || '',
      due_date: st.due_date ? st.due_date.split('T')[0] : '',
    });
  };

  const handleSaveSubtask = async () => {
    if (!editingSubtaskId || !editSubtaskForm.title.trim()) return;
    try {
      setSavingSubtask(true);
      await taskApi.update(editingSubtaskId, {
        title: editSubtaskForm.title,
        assignee_id: editSubtaskForm.assignee_id || undefined,
        due_date: editSubtaskForm.due_date || undefined,
      });
      setEditingSubtaskId(null);
      await fetchSubtasks();
      onUpdated?.();
    } catch { /* ignore */ }
    finally { setSavingSubtask(false); }
  };

  const toLocalDateTimeInput = (iso: string) => {
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const handleAddReminder = async () => {
    if (!taskId || !newReminderAt) return;
    try {
      setSavingReminder(true);
      await notificationApi.createReminder(taskId, {
        scheduled_time: new Date(newReminderAt).toISOString(),
        method: newReminderMethod,
      });
      setNewReminderAt('');
      setShowAddReminder(false);
      await fetchReminders();
    } catch { /* ignore */ }
    finally { setSavingReminder(false); }
  };

  const handleDismissReminder = async (reminderId: string) => {
    try {
      await notificationApi.dismissReminder(reminderId);
      setReminders((prev) => prev.filter((reminder) => reminder.id !== reminderId));
      setReminderAction({ type: 'success', message: 'Reminder dismissed and removed.' });
      await fetchReminders();
    } catch (err) {
      console.error('Failed to dismiss reminder:', err);
      setReminderAction({ type: 'error', message: 'Failed to dismiss reminder. Please try again.' });
    }
  };

  const openSubtaskModal = (
    subtaskId: string,
    tab: 'details' | 'reminders' | 'subtasks' | 'comments' | 'activity' = 'details',
  ) => {
    setSubtaskModalTaskId(subtaskId);
    setSubtaskModalInitialTab(tab);
  };

  const startEditingReminder = (reminder: TaskReminder) => {
    setEditingReminderId(reminder.id);
    setEditingReminderAt(toLocalDateTimeInput(reminder.scheduled_time));
  };

  const handleRescheduleReminder = async () => {
    if (!editingReminderId || !editingReminderAt) return;
    try {
      await notificationApi.snoozeReminder(editingReminderId, new Date(editingReminderAt).toISOString());
      setEditingReminderId(null);
      setEditingReminderAt('');
      await fetchReminders();
    } catch { /* ignore */ }
  };

  const formatDate = (d?: string) => d ? new Date(d).toLocaleDateString() : '-';
  const formatDateTime = (d: string) => new Date(d).toLocaleString();
  const formatReminderMethod = (method: ReminderMethod) => {
    switch (method) {
      case 'in_app': return 'In-App';
      case 'email': return 'Email';
      case 'push': return 'Push';
      case 'sms': return 'SMS';
      default: return method;
    }
  };

  type SimpleStatus = 'ok' | 'delayed' | 'issue' | 'unknown' | 'unused';

  const statusClasses = (status: SimpleStatus) => {
    if (isEdgeMail) {
      switch (status) {
        case 'ok':
          return 'bg-[var(--em-status-success-bg)] text-[var(--em-status-success-text)]';
        case 'delayed':
          return 'bg-[var(--em-status-warning-bg)] text-[var(--em-status-warning-text)]';
        case 'issue':
          return 'bg-[var(--em-status-danger-bg)] text-[var(--em-status-danger-text)]';
        case 'unused':
          return 'bg-[var(--em-surface-toolbar)] text-[var(--em-text-tertiary)]';
        default:
          return 'bg-[var(--em-surface-toolbar)] text-[var(--em-text-muted)]';
      }
    }
    switch (status) {
      case 'ok':
        return 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300';
      case 'delayed':
        return 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300';
      case 'issue':
        return 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300';
      case 'unused':
        return 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400';
      default:
        return 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-500';
    }
  };

  const statusLabel = (status: SimpleStatus) => {
    switch (status) {
      case 'ok':
        return 'Working';
      case 'delayed':
        return 'Delayed';
      case 'issue':
        return 'Issue';
      case 'unused':
        return 'Not set';
      default:
        return 'Unknown';
    }
  };

  const latestSentReminder = useMemo(() => {
    const withSent = reminders.filter((r) => r.sent_time);
    if (withSent.length === 0) return null;
    return withSent.sort((a, b) => new Date(b.sent_time || 0).getTime() - new Date(a.sent_time || 0).getTime())[0];
  }, [reminders]);

  const latestReminderByMethod = useCallback((method: ReminderMethod) => {
    const list = reminders.filter((r) => r.method === method);
    if (list.length === 0) return null;
    return list.sort((a, b) => new Date(b.scheduled_time).getTime() - new Date(a.scheduled_time).getTime())[0];
  }, [reminders]);

  const buildMethodStatus = useCallback((method: ReminderMethod): { label: string; status: SimpleStatus; detail?: string } => {
    const latest = latestReminderByMethod(method);
    if (!latest) return { label: formatReminderMethod(method), status: 'unused', detail: 'Not configured' };
    if (latest.status === 'failed') {
      return { label: formatReminderMethod(method), status: 'issue', detail: latest.failure_reason || 'Delivery failed' };
    }
    if (latest.status === 'pending' || latest.status === 'snoozed') {
      return { label: formatReminderMethod(method), status: 'delayed', detail: 'Scheduled' };
    }
    if (latest.status === 'sent') {
      return { label: formatReminderMethod(method), status: 'ok', detail: latest.sent_time ? `Last sent ${formatDateTime(latest.sent_time)}` : undefined };
    }
    return { label: formatReminderMethod(method), status: 'unknown' };
  }, [latestReminderByMethod]);

  const healthSummary = useMemo(() => {
    if (reminderHealthLoading && !reminderHealth) {
      return {
        headline: 'Checking reminder health',
        status: 'unknown' as SimpleStatus,
        detail: 'Fetching the latest delivery status.',
        scheduling: 'unknown' as SimpleStatus,
        inApp: 'unknown' as SimpleStatus,
      };
    }
    if (!reminderHealth) {
      return {
        headline: 'Health status unavailable',
        status: 'unknown' as SimpleStatus,
        detail: 'We could not retrieve system health data.',
        scheduling: 'unknown' as SimpleStatus,
        inApp: 'unknown' as SimpleStatus,
      };
    }

    const hasHardFailure =
      reminderHealth.redis.status === 'unhealthy'
      || reminderHealth.queue.status === 'unhealthy'
      || reminderHealth.poller.status === 'unhealthy';

    const schedulingStatus: SimpleStatus = hasHardFailure
      ? 'issue'
      : reminderHealth.poller.status === 'degraded'
        ? 'delayed'
        : 'ok';

    const inAppStatus: SimpleStatus = reminderHealth.websocket.status === 'healthy' ? 'ok' : 'delayed';

    const issues: string[] = [];
    if (reminderHealth.redis.status === 'unhealthy') issues.push('Scheduling storage is unavailable.');
    if (reminderHealth.queue.status === 'unhealthy') issues.push('Reminder queue is unavailable.');
    if (reminderHealth.poller.status === 'unhealthy') issues.push('Reminder scheduler is offline.');
    if (reminderHealth.poller.status === 'degraded') issues.push('Reminder scheduler is idle.');
    if (reminderHealth.websocket.status === 'degraded') issues.push('Live alerts may be delayed.');

    const emailStatus = buildMethodStatus('email');
    const smsStatus = buildMethodStatus('sms');
    if (emailStatus.status === 'issue') issues.push('Email delivery failed.');
    if (smsStatus.status === 'issue') issues.push('SMS delivery failed.');

    if (hasHardFailure) {
      return {
        headline: 'Reminders are not sending',
        status: 'issue' as SimpleStatus,
        detail: issues[0] || 'The reminder system is offline.',
        scheduling: schedulingStatus,
        inApp: inAppStatus,
      };
    }

    if (issues.length > 0) {
      return {
        headline: 'Reminders may be delayed',
        status: 'delayed' as SimpleStatus,
        detail: issues[0],
        scheduling: schedulingStatus,
        inApp: inAppStatus,
      };
    }

    return {
      headline: 'Reminders are working',
      status: 'ok' as SimpleStatus,
      detail: 'All systems operational.',
      scheduling: schedulingStatus,
      inApp: inAppStatus,
    };
  }, [reminderHealth, reminderHealthLoading, buildMethodStatus]);

  const pollerDetail = useMemo(() => {
    if (!reminderHealth) return undefined;
    if (reminderHealth.poller.lastPollAt) {
      return `Last poll ${formatDateTime(reminderHealth.poller.lastPollAt)}`;
    }
    if (typeof reminderHealth.poller.pendingCount === 'number') {
      return `${reminderHealth.poller.pendingCount} pending reminders`;
    }
    if (reminderHealth.poller.error === 'no_heartbeat_no_pending') {
      return 'Idle (no pending reminders)';
    }
    return reminderHealth.poller.error || undefined;
  }, [reminderHealth]);

  const inAppDetail = useMemo(() => {
    if (!reminderHealth) return undefined;
    if (reminderHealth.websocket.mode === 'unavailable') return 'Live alerts unavailable';
    if (reminderHealth.websocket.status === 'degraded') return 'Live alerts delayed';
    return 'Live alerts connected';
  }, [reminderHealth]);

  const methodStatusItems = useMemo(() => {
    const emailStatus = buildMethodStatus('email');
    const smsStatus = buildMethodStatus('sms');
    const pushStatus = buildMethodStatus('push');
    return [
      { label: 'Scheduling', status: healthSummary.scheduling, detail: pollerDetail },
      { label: 'In-App', status: healthSummary.inApp, detail: inAppDetail },
      emailStatus,
      smsStatus,
      pushStatus,
    ];
  }, [healthSummary.scheduling, healthSummary.inApp, pollerDetail, inAppDetail, buildMethodStatus]);
  const displayName = (u?: { full_name?: string; first_name?: string; last_name?: string; email?: string } | null) => {
    if (!u) return 'Unknown';
    if (u.full_name) return u.full_name;
    if (u.first_name) return `${u.first_name} ${u.last_name || ''}`.trim();
    return u.email || 'Unknown';
  };

  const activityConfig: Record<string, { icon: React.ElementType; color: string; bg: string; label: string }> = isEdgeMail
    ? {
        created:             { icon: Plus,          color: 'text-[var(--em-status-success-text)]', bg: 'bg-[var(--em-status-success-bg)]', label: 'created this task' },
        updated:             { icon: Pencil,        color: 'text-[var(--em-accent-primary)]',      bg: 'bg-[var(--em-accent-primary-light)]', label: 'updated' },
        deleted:             { icon: Trash2,        color: 'text-[var(--em-status-danger-text)]',  bg: 'bg-[var(--em-status-danger-bg)]', label: 'deleted this task' },
        status_changed:      { icon: ArrowRightLeft,color: 'text-[var(--em-status-warning-text)]', bg: 'bg-[var(--em-status-warning-bg)]', label: 'changed status' },
        assigned:            { icon: UserCheck,     color: 'text-[var(--em-accent-primary)]',      bg: 'bg-[var(--em-accent-primary-light)]', label: 'assigned' },
        reassigned:          { icon: UserPlus,      color: 'text-[var(--em-accent-primary)]',      bg: 'bg-[var(--em-accent-primary-light)]', label: 'reassigned' },
        commented:           { icon: MessageSquare, color: 'text-[var(--em-text-tertiary)]',       bg: 'bg-[var(--em-surface-toolbar)]', label: 'commented' },
        attachment_added:    { icon: Paperclip,     color: 'text-[var(--em-accent-primary)]',      bg: 'bg-[var(--em-accent-primary-light)]', label: 'added an attachment' },
        attachment_removed:  { icon: Paperclip,     color: 'text-[var(--em-status-danger-text)]',  bg: 'bg-[var(--em-status-danger-bg)]', label: 'removed an attachment' },
        dependency_added:    { icon: Link2,         color: 'text-[var(--em-accent-primary)]',      bg: 'bg-[var(--em-accent-primary-light)]', label: 'added a dependency' },
        dependency_removed:  { icon: Unlink,        color: 'text-[var(--em-status-warning-text)]', bg: 'bg-[var(--em-status-warning-bg)]', label: 'removed a dependency' },
        completed:           { icon: CheckCircle2,  color: 'text-[var(--em-status-success-text)]', bg: 'bg-[var(--em-status-success-bg)]', label: 'completed this task' },
        reopened:            { icon: RotateCcw,     color: 'text-[var(--em-status-warning-text)]', bg: 'bg-[var(--em-status-warning-bg)]', label: 'reopened this task' },
      }
    : {
        created:             { icon: Plus,          color: 'text-green-500',  bg: 'bg-green-100 dark:bg-green-900/30',  label: 'created this task' },
        updated:             { icon: Pencil,        color: 'text-lex-accent',   bg: 'bg-lex-accent-soft',    label: 'updated' },
        deleted:             { icon: Trash2,        color: 'text-red-500',    bg: 'bg-red-100 dark:bg-red-900/30',      label: 'deleted this task' },
        status_changed:      { icon: ArrowRightLeft,color: 'text-purple-500', bg: 'bg-purple-100 dark:bg-purple-900/30',label: 'changed status' },
        assigned:            { icon: UserCheck,     color: 'text-lex-accent', bg: 'bg-lex-accent-soft',label: 'assigned' },
        reassigned:          { icon: UserPlus,      color: 'text-lex-accent', bg: 'bg-lex-accent-soft',label: 'reassigned' },
        commented:           { icon: MessageSquare, color: 'text-gray-500',   bg: 'bg-gray-100 dark:bg-gray-700/30',    label: 'commented' },
        attachment_added:    { icon: Paperclip,     color: 'text-teal-500',   bg: 'bg-teal-100 dark:bg-teal-900/30',    label: 'added an attachment' },
        attachment_removed:  { icon: Paperclip,     color: 'text-orange-500', bg: 'bg-orange-100 dark:bg-orange-900/30', label: 'removed an attachment' },
        dependency_added:    { icon: Link2,         color: 'text-cyan-500',   bg: 'bg-cyan-100 dark:bg-cyan-900/30',    label: 'added a dependency' },
        dependency_removed:  { icon: Unlink,        color: 'text-orange-500', bg: 'bg-orange-100 dark:bg-orange-900/30', label: 'removed a dependency' },
        completed:           { icon: CheckCircle2,  color: 'text-green-600',  bg: 'bg-green-100 dark:bg-green-900/30',  label: 'completed this task' },
        reopened:            { icon: RotateCcw,     color: 'text-amber-500',  bg: 'bg-amber-100 dark:bg-amber-900/30',  label: 'reopened this task' },
      };
  const defaultActivity = isEdgeMail
    ? { icon: AlertCircle, color: 'text-[var(--em-text-tertiary)]', bg: 'bg-[var(--em-surface-toolbar)]', label: 'performed an action' }
    : { icon: AlertCircle, color: 'text-gray-400', bg: 'bg-gray-100 dark:bg-gray-700/30', label: 'performed an action' };

  const getActivityDescription = (log: TaskAuditLog): string => {
    const cfg = activityConfig[log.action] || defaultActivity;
    if (log.action === 'updated' && log.field_name) {
      if (log.field_name === 'assignment_notification') return 'sent assignment notification email';
      if (log.field_name === 'assignment_status') {
        if (log.new_value === 'accepted') return 'accepted the task assignment';
        if (log.new_value === 'rejected') return 'declined the task assignment';
        return `changed assignment status to "${log.new_value}"`;
      }
      if (log.field_name === 'tags') {
        if (log.new_value && !log.old_value) return `added tags: ${log.new_value}`;
        if (log.old_value && log.new_value) return `updated tags to: ${log.new_value}`;
        if (log.old_value && !log.new_value) return 'removed all tags';
        return 'updated tags';
      }
      const field = log.field_name.replace(/_/g, ' ');
      // Format date values for display
      const formatVal = (val: string) => {
        if (/^\d{4}-\d{2}-\d{2}/.test(val) || /GMT|UTC|Coordinated/.test(val)) {
          try { return new Date(val).toLocaleDateString(); } catch { return val; }
        }
        return val;
      };
      if (log.old_value && log.new_value) return `changed ${field} from "${formatVal(log.old_value)}" to "${formatVal(log.new_value)}"`;
      if (log.new_value) return `set ${field} to "${formatVal(log.new_value)}"`;
      return `updated ${field}`;
    }
    if (log.action === 'status_changed') {
      if (log.old_value && log.new_value) return `changed status from "${log.old_value.replace(/_/g, ' ')}" to "${log.new_value.replace(/_/g, ' ')}"`;
      if (log.new_value) return `set status to "${log.new_value.replace(/_/g, ' ')}"`;
      return cfg.label;
    }
    return cfg.label;
  };

  const formatRelativeTime = (dateStr: string): string => {
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDays = Math.floor(diffHr / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getStatusColor = (status: string) => {
    if (isEdgeMail) {
      switch (status) {
        case 'done': return 'bg-[var(--em-status-success-bg)] text-[var(--em-status-success-text)]';
        case 'in_progress': return 'bg-[var(--em-accent-primary-light)] text-[var(--em-accent-primary)]';
        case 'review': return 'bg-[var(--em-status-warning-bg)] text-[var(--em-status-warning-text)]';
        case 'archived': return 'bg-[var(--em-surface-toolbar)] text-[var(--em-text-tertiary)]';
        case 'todo': return 'bg-[var(--em-surface-toolbar)] text-[var(--em-text-secondary)]';
        default: return 'bg-[var(--em-surface-toolbar)] text-[var(--em-text-tertiary)]';
      }
    }
    switch (status) {
      case 'done': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
      case 'in_progress': return 'bg-lex-accent-soft text-lex-accent';
      case 'review': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300';
      case 'archived': return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300';
      case 'todo': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        {/* Backdrop */}
        <div className={cn('fixed inset-0 transition-opacity', backdropClass)} onClick={onClose} />

        {/* Modal */}
        <div className={modalShellClass}>
          {/* Header */}
          <div className={headerClass}>
            <h2 className={cn('text-lg font-semibold', titleClass)}>
              {isCreateMode ? 'Create Task' : loading ? 'Loading...' : task?.title || 'Task not found'}
            </h2>
            <button onClick={onClose} className={closeButtonClass}>
              <XMarkIcon className={cn('h-5 w-5', isEdgeMail ? 'text-[var(--em-text-tertiary)]' : 'text-gray-500')} />
            </button>
          </div>

          {loading && !isCreateMode ? (
            <div className="flex items-center justify-center py-16">
              <div
                className={cn(
                  'animate-spin rounded-full h-8 w-8 border-b-2',
                  isEdgeMail ? 'border-[var(--em-accent-primary)]' : 'border-lex-accent',
                )}
              />
            </div>
          ) : !task && !isCreateMode ? (
            <div className={cn('text-center py-16', textTertiary)}>Task not found.</div>
          ) : (
            <>
              {/* Tabs */}
              <div className={tabBarClass}>
                {((
                  ['details', 'reminders', 'subtasks', 'comments', 'activity'] as const
                ).filter(tab => tab !== 'reminders' || !!taskId) as Array<'details' | 'reminders' | 'subtasks' | 'comments' | 'activity'>).map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={cn(
                      'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors rounded-t-md',
                      activeTab === tab ? tabActiveClass : tabInactiveClass,
                    )}
                  >
                    {tab === 'details' ? 'Details'
                      : tab === 'reminders' ? `Reminders (${reminders.length})`
                      : tab === 'subtasks' ? `Subtasks (${subtaskProgress.completed}/${subtaskProgress.total})`
                      : tab === 'comments' ? `Comments (${comments.length})`
                      : 'Activity'}
                  </button>
                ))}
              </div>

              {/* Content */}
              <div className="overflow-y-auto max-h-[60vh] p-6">
                <div key={activeTab} className="lex-tab-panel">
                {activeTab === 'details' && (
                  <div className="space-y-6">
                    {/* Status + Priority row (only for existing tasks) */}
                    {task && (
                      <div className="flex flex-wrap gap-3 items-center">
                        <select
                          value={task.status}
                          onChange={(e) => handleStatusChange(e.target.value as TaskStatus)}
                          className={cn(
                            'px-3 py-1.5 text-sm rounded-lg border',
                            isEdgeMail ? 'border-[var(--em-border-secondary)]' : 'border-gray-300 dark:border-gray-600 dark:text-gray-100',
                            getStatusColor(task.status),
                          )}
                        >
                          {STATUS_OPTIONS.map(o => (
                            <option
                              key={o.value}
                              value={o.value}
                              className={isEdgeMail ? 'bg-[var(--em-surface-card)] text-[var(--em-text-primary)]' : 'bg-white dark:bg-gray-700 dark:text-gray-100 text-gray-900'}
                            >
                              {o.label}
                            </option>
                          ))}
                        </select>
                        <span
                          className={cn(
                            'px-2 py-1 text-xs rounded-full',
                            isEdgeMail ? 'bg-[var(--em-surface-toolbar)] text-[var(--em-text-secondary)]' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
                          )}
                        >
                          {task.priority} priority
                        </span>
                        {task.due_date && (
                          <span className={cn('text-sm', textTertiary)}>Due: {formatDate(task.due_date)}</span>
                        )}
                        {!editing && (
                          <button
                            onClick={startEditing}
                            className={cn('ml-auto px-3 py-1 text-sm rounded', secondaryButtonClass)}
                          >
                            Edit
                          </button>
                        )}
                      </div>
                    )}

                    {editing ? (
                      <div className="space-y-4">
                        <div>
                          <label className={cn('block text-sm font-medium mb-1', textSecondary)}>Title</label>
                          <input
                            type="text"
                            value={editForm.title || ''}
                            onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                            className={cn('w-full px-3 py-2 rounded-lg border', inputClass)}
                          />
                        </div>
                        <div>
                          <label className={cn('block text-sm font-medium mb-1', textSecondary)}>Description</label>
                          <textarea
                            rows={4}
                            value={editForm.description || ''}
                            onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                            className={cn('w-full px-3 py-2 rounded-lg border', inputClass)}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className={cn('block text-sm font-medium mb-1', textSecondary)}>Priority</label>
                            <select
                              value={editForm.priority || 'medium'}
                              onChange={(e) => setEditForm(prev => ({ ...prev, priority: e.target.value as TaskPriority }))}
                              className={cn('w-full px-3 py-2 rounded-lg border', selectClass)}
                            >
                              {PRIORITY_OPTIONS.map(o => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className={cn('block text-sm font-medium mb-1', textSecondary)}>Due Date</label>
                            <input
                              type="date"
                              value={editForm.due_date || ''}
                              onChange={(e) => setEditForm(prev => ({ ...prev, due_date: e.target.value }))}
                              className={cn('w-full px-3 py-2 rounded-lg border', inputClass)}
                            />
                          </div>
                        </div>
                        {/* Tags */}
                        <div>
                          <label className={cn('block text-sm font-medium mb-1', textSecondary)}>Tags</label>
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            {(editForm.tags || []).map(tag => (
                              <span key={tag} className={cn('inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full', accentChipClass)}>
                                {tag}
                                <button
                                  type="button"
                                  onClick={() => setEditForm(prev => ({ ...prev, tags: (prev.tags || []).filter(t => t !== tag) }))}
                                  className={cn(accentTextClass, accentTextHoverClass)}
                                >
                                  &times;
                                </button>
                              </span>
                            ))}
                          </div>
                          <input
                            id="tag-input-field"
                            type="text"
                            placeholder="Type a tag and press Enter..."
                            className={cn('w-full px-3 py-2 rounded-lg border text-sm', inputClass)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                e.stopPropagation();
                                const val = (e.target as HTMLInputElement).value.trim();
                                if (val && !(editForm.tags || []).includes(val)) {
                                  setEditForm(prev => ({ ...prev, tags: [...(prev.tags || []), val] }));
                                  (e.target as HTMLInputElement).value = '';
                                }
                              }
                            }}
                          />
                        </div>
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={isCreateMode ? onClose : () => setEditing(false)}
                            className={cn('px-4 py-2 text-sm rounded-lg', secondaryButtonClass)}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={isCreateMode ? handleCreate : handleSave}
                            disabled={saving}
                            className={cn('px-4 py-2 text-sm rounded-lg disabled:opacity-50', primaryButtonClass)}
                          >
                            {saving ? (isCreateMode ? 'Creating...' : 'Saving...') : isCreateMode ? 'Create Task' : 'Save Changes'}
                          </button>
                        </div>
                      </div>
                    ) : task ? (
                      <>
                        {task.description && (
                          <div>
                            <h4 className={cn('text-sm font-medium mb-1', textTertiary)}>Description</h4>
                            <p className={cn('whitespace-pre-wrap', textPrimary)}>{task.description}</p>
                          </div>
                        )}
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div className="col-span-2 flex items-center gap-3 flex-wrap">
                            <span className={textTertiary}>Assignee:</span>
                            <span className={textPrimary}>{task.assignee ? displayName(task.assignee) : 'Unassigned'}</span>
                            {task.assignment_status && (
                              <span
                                className={cn(
                                  'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                                  isEdgeMail
                                    ? task.assignment_status === 'accepted'
                                      ? 'bg-[var(--em-status-success-bg)] text-[var(--em-status-success-text)]'
                                      : task.assignment_status === 'rejected'
                                        ? 'bg-[var(--em-status-danger-bg)] text-[var(--em-status-danger-text)]'
                                        : 'bg-[var(--em-status-warning-bg)] text-[var(--em-status-warning-text)]'
                                    : task.assignment_status === 'accepted'
                                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                      : task.assignment_status === 'rejected'
                                        ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
                                )}
                              >
                                {task.assignment_status === 'accepted' ? 'Accepted' : task.assignment_status === 'rejected' ? 'Declined' : 'Pending'}
                              </span>
                            )}
                            {task.assignee_id && !task.assignment_status && (
                              <button
                                onClick={handleNotifyAssignee}
                                disabled={notifying}
                                className={cn(
                                  'inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md transition-colors disabled:opacity-50',
                                  isEdgeMail
                                    ? 'text-[var(--em-accent-primary)] bg-[var(--em-accent-primary-light)] hover:bg-[var(--em-state-hover)]'
                                    : 'text-lex-accent bg-lex-accent-soft hover:bg-lex-state-hover',
                                )}
                              >
                                {notifying ? 'Sending...' : 'Notify Assignee'}
                              </button>
                            )}
                            {(task.assignment_status === 'accepted' || task.assignment_status === 'rejected') && (
                              <button
                                onClick={() => setShowReassign(!showReassign)}
                                className={cn(
                                  'inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md transition-colors',
                                  isEdgeMail
                                    ? 'text-[var(--em-accent-primary)] bg-[var(--em-accent-primary-light)] hover:bg-[var(--em-state-hover)]'
                                    : 'text-lex-accent bg-lex-accent-soft hover:bg-lex-state-hover',
                                )}
                              >
                                Reassign
                              </button>
                            )}
                          </div>
                          {showReassign && (
                            <div
                              className={cn(
                                'col-span-2 p-3 rounded-lg border',
                                isEdgeMail ? 'bg-[var(--em-surface-toolbar)] border-[var(--em-border-secondary)]' : 'bg-lex-accent-soft border-lex-accent',
                              )}
                            >
                              <label className={cn('block text-xs font-medium mb-2', isEdgeMail ? 'text-[var(--em-text-secondary)]' : 'text-lex-accent')}>
                                Reassign to:
                              </label>
                              <div className="flex gap-2">
                                <select
                                  defaultValue=""
                                  onChange={(e) => { if (e.target.value) handleReassign(e.target.value); }}
                                  disabled={reassigning}
                                  className={cn('flex-1 px-3 py-1.5 rounded-lg border text-sm', isEdgeMail ? selectClass : 'border-lex-accent bg-white dark:bg-gray-700 text-gray-900 dark:text-white')}
                                >
                                  <option value="" disabled>Select a user...</option>
                                  {attorneys.filter(u => (u.keycloakUserId || u.id) !== task.assignee_id).map(u => (
                                    <option key={u.id} value={u.keycloakUserId || u.id}>{u.name}</option>
                                  ))}
                                </select>
                                <button
                                  onClick={() => setShowReassign(false)}
                                  className={cn('px-3 py-1.5 text-xs rounded-lg', secondaryButtonClass)}
                                >
                                  Cancel
                                </button>
                              </div>
                              {reassigning && (
                                <p className={cn('text-xs mt-1', isEdgeMail ? 'text-[var(--em-accent-primary)]' : 'text-lex-accent')}>
                                  Reassigning...
                                </p>
                              )}
                            </div>
                          )}
                          {task.assignment_status === 'rejected' && task.assignment_rejection_reason && (
                            <div
                              className={cn(
                                'col-span-2 p-3 rounded-lg border',
                                isEdgeMail
                                  ? 'bg-[var(--em-status-danger-bg)] border-[var(--em-border-secondary)]'
                                  : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
                              )}
                            >
                              <span className={cn('text-xs font-medium', isEdgeMail ? 'text-[var(--em-status-danger-text)]' : 'text-red-700 dark:text-red-300')}>
                                Decline reason:
                              </span>
                              <p className={cn('text-sm mt-1', isEdgeMail ? 'text-[var(--em-status-danger-text)]' : 'text-red-600 dark:text-red-400')}>
                                {task.assignment_rejection_reason}
                              </p>
                            </div>
                          )}
                          <div>
                            <span className={textTertiary}>Source:</span>
                            <span className={cn('ml-2', textPrimary)}>{task.source.replace(/_/g, ' ')}</span>
                          </div>
                          <div>
                            <span className={textTertiary}>Created:</span>
                            <span className={cn('ml-2', textPrimary)}>{formatDateTime(task.created_at)}</span>
                          </div>
                          <div>
                            <span className={textTertiary}>Updated:</span>
                            <span className={cn('ml-2', textPrimary)}>{formatDateTime(task.updated_at)}</span>
                          </div>
                          {task.estimated_hours != null && (
                            <div>
                              <span className={textTertiary}>Est. hours:</span>
                              <span className={cn('ml-2', textPrimary)}>{task.estimated_hours}h</span>
                            </div>
                          )}
                          {task.actual_hours != null && (
                            <div>
                              <span className={textTertiary}>Actual hours:</span>
                              <span className={cn('ml-2', textPrimary)}>{task.actual_hours}h</span>
                            </div>
                          )}
                        </div>
                        {task.tags && task.tags.length > 0 && (
                          <div>
                            <h4 className={cn('text-sm font-medium mb-1', textTertiary)}>Tags</h4>
                            <div className="flex flex-wrap gap-1">
                              {task.tags.map(tag => (
                                <span
                                  key={tag}
                                  className={cn(
                                    'px-2 py-0.5 text-xs rounded',
                                    isEdgeMail ? 'bg-[var(--em-surface-toolbar)] text-[var(--em-text-secondary)]' : 'bg-gray-200 dark:bg-gray-700',
                                  )}
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {subtaskProgress.total > 0 && (
                          <div
                            className={cn(
                              'cursor-pointer p-2 -mx-2 rounded-lg transition-colors',
                              isEdgeMail ? 'hover:bg-[var(--em-state-hover)]' : 'hover:bg-gray-50 dark:hover:bg-gray-700',
                            )}
                            onClick={() => setActiveTab('subtasks')}
                          >
                            <h4 className={cn('text-sm font-medium mb-1', textTertiary)}>Subtasks</h4>
                            <div className="flex items-center gap-2">
                              <div className={cn('flex-1 rounded-full h-2', isEdgeMail ? 'bg-[var(--em-border-secondary)]' : 'bg-gray-200 dark:bg-gray-700')}>
                                <div
                                  className={cn('h-2 rounded-full transition-all', isEdgeMail ? 'bg-[var(--em-accent-primary)]' : 'bg-lex-accent')}
                                  style={{ width: `${(subtaskProgress.completed / subtaskProgress.total) * 100}%` }}
                                />
                              </div>
                              <span className={cn('text-sm', textTertiary)}>
                                {subtaskProgress.completed}/{subtaskProgress.total}
                              </span>
                            </div>
                          </div>
                        )}
                      </>
                    ) : null}

                    {/* Playbook status (if task has a playbook instance) */}
                    {task?.id && (
                      <TaskPlaybookPanel taskId={task.id} />
                    )}
                  </div>
                )}

                {activeTab === 'reminders' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className={cn('text-sm font-medium', textSecondary)}>Reminders</h4>
                      <button
                        onClick={() => setShowAddReminder((prev) => !prev)}
                        className={cn('px-3 py-1 text-xs rounded-lg', secondaryButtonClass)}
                      >
                        {showAddReminder ? 'Close' : 'Add Reminder'}
                      </button>
                    </div>

                    <div
                      className={cn(
                        'rounded-lg border p-3 space-y-2',
                        isEdgeMail ? 'border-[var(--em-border-secondary)] bg-[var(--em-surface-card)]' : 'border-gray-200 dark:border-gray-700',
                      )}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span
                              className={cn(
                                'px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide',
                                statusClasses(healthSummary.status),
                              )}
                            >
                              {statusLabel(healthSummary.status)}
                            </span>
                            <span className={cn('text-sm font-medium', textSecondary)}>{healthSummary.headline}</span>
                          </div>
                          <div className={cn('text-xs', textTertiary)}>{healthSummary.detail}</div>
                          {latestSentReminder && (
                            <div className={cn('text-xs', textTertiary)}>
                              Last reminder sent {formatDateTime(latestSentReminder.sent_time || latestSentReminder.scheduled_time)}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={fetchReminderHealth}
                            className={cn('px-2 py-1 text-xs rounded', secondaryButtonClass)}
                          >
                            Refresh
                          </button>
                          <button
                            onClick={() => setShowHealthDetails((prev) => !prev)}
                            className={cn('px-2 py-1 text-xs rounded', secondaryButtonClass)}
                          >
                            {showHealthDetails ? 'Hide' : 'Advanced'}
                          </button>
                        </div>
                      </div>

                      {reminderHealthLoading && (
                        <div className={cn('text-xs', textTertiary)}>Refreshing status...</div>
                      )}

                      <div className="grid grid-cols-2 gap-2">
                        {methodStatusItems.map((item) => (
                          <div
                            key={item.label}
                            className={cn(
                              'rounded-md border px-2 py-1.5 space-y-1',
                              isEdgeMail
                                ? 'border-[var(--em-border-secondary)] bg-[var(--em-surface-toolbar)]'
                                : 'border-gray-200 dark:border-gray-700',
                            )}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span className={cn('text-xs font-medium', textSecondary)}>{item.label}</span>
                              <span
                                className={cn(
                                  'px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide',
                                  statusClasses(item.status),
                                )}
                              >
                                {statusLabel(item.status)}
                              </span>
                            </div>
                            {item.detail && <div className={cn('text-[11px]', textTertiary)}>{item.detail}</div>}
                          </div>
                        ))}
                      </div>

                      {!reminderHealth && !reminderHealthLoading && (
                        <div className={cn('text-xs', textTertiary)}>Health data unavailable.</div>
                      )}

                      {showHealthDetails && reminderHealth && (
                        <div className="space-y-1 pt-2">
                          {renderHealthBadge('Redis', reminderHealth.redis.status, reminderHealth.redis.error)}
                          {renderHealthBadge('Queue', reminderHealth.queue.status, reminderHealth.queue.error)}
                          {renderHealthBadge(
                            'Poller',
                            reminderHealth.poller.status,
                            reminderHealth.poller.lastPollAt
                              ? `Last poll: ${reminderHealth.poller.lastPollAt}`
                              : reminderHealth.poller.error === 'no_heartbeat_no_pending'
                                ? 'Idle (no pending reminders)'
                                : reminderHealth.poller.error,
                          )}
                          {renderHealthBadge(
                            'WebSocket',
                            reminderHealth.websocket.status,
                            `Mode: ${reminderHealth.websocket.mode}`,
                          )}
                        </div>
                      )}
                    </div>

                    {showAddReminder && (
                      <div
                        className={cn(
                          'rounded-lg p-3 border space-y-2',
                          isEdgeMail ? 'bg-[var(--em-surface-toolbar)] border-[var(--em-border-secondary)]' : 'bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700',
                        )}
                      >
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className={cn('block text-xs font-medium mb-1', textSecondary)}>When</label>
                            <input
                              type="datetime-local"
                              value={newReminderAt}
                              onChange={(e) => setNewReminderAt(e.target.value)}
                              className={cn('w-full px-2 py-1.5 rounded-lg border text-sm', inputClass)}
                            />
                          </div>
                          <div>
                            <label className={cn('block text-xs font-medium mb-1', textSecondary)}>Method</label>
                            <select
                              value={newReminderMethod}
                              onChange={(e) => setNewReminderMethod(e.target.value as ReminderMethod)}
                              className={cn('w-full px-2 py-1.5 rounded-lg border text-sm', selectClass)}
                            >
                              <option value="in_app">In-App</option>
                              <option value="email">Email</option>
                              <option value="push">Push</option>
                              <option value="sms">SMS</option>
                            </select>
                          </div>
                        </div>
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => { setShowAddReminder(false); setNewReminderAt(''); }}
                            className={cn('px-3 py-1 text-xs rounded-lg', secondaryButtonClass)}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleAddReminder}
                            disabled={savingReminder || !newReminderAt}
                            className={cn('px-3 py-1 text-xs rounded-lg disabled:opacity-50', primaryButtonClass)}
                          >
                            {savingReminder ? 'Adding...' : 'Add'}
                          </button>
                        </div>
                      </div>
                    )}

                    {reminderAction && (
                      <div
                        className={cn(
                          'rounded-lg border px-3 py-2 text-xs',
                          reminderAction.type === 'success'
                            ? isEdgeMail
                              ? 'border-[var(--em-status-success-bg)] bg-[var(--em-status-success-bg)] text-[var(--em-status-success-text)]'
                              : 'border-green-200 bg-green-50 text-green-700 dark:border-green-900/40 dark:bg-green-900/20 dark:text-green-300'
                            : isEdgeMail
                              ? 'border-[var(--em-status-danger-bg)] bg-[var(--em-status-danger-bg)] text-[var(--em-status-danger-text)]'
                              : 'border-red-200 bg-red-50 text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300',
                        )}
                      >
                        {reminderAction.message}
                      </div>
                    )}

                    {remindersLoading ? (
                      <div className={cn('text-center py-6', textTertiary)}>Loading reminders...</div>
                    ) : reminders.length === 0 ? (
                      <div className={cn('text-center py-6', textTertiary)}>No reminders set.</div>
                    ) : (
                      <div className="space-y-2">
                        {reminders.map((reminder) => (
                          <div
                            key={reminder.id}
                            className={cn(
                              'rounded-lg border p-3',
                              isEdgeMail ? 'border-[var(--em-border-secondary)] bg-[var(--em-surface-card)]' : 'border-gray-200 dark:border-gray-700',
                            )}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <div className={cn('text-sm font-medium', textPrimary)}>
                                  {formatReminderMethod(reminder.method)} · {formatDateTime(reminder.scheduled_time)}
                                </div>
                                <div className={cn('text-xs mt-1', textTertiary)}>
                                  Status: {reminder.status} · Source: {reminder.source}
                                </div>
                                {renderDeliveryDetails(reminder)}
                              </div>
                              <div className="flex gap-2">
                                {editingReminderId === reminder.id ? (
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="datetime-local"
                                      value={editingReminderAt}
                                      onChange={(e) => setEditingReminderAt(e.target.value)}
                                      className={cn('px-2 py-1 text-xs rounded border', inputClass)}
                                    />
                                    <button
                                      onClick={handleRescheduleReminder}
                                      className={cn('px-2 py-1 text-xs rounded', primaryButtonClass)}
                                    >
                                      Save
                                    </button>
                                    <button
                                      onClick={() => { setEditingReminderId(null); setEditingReminderAt(''); }}
                                      className={cn('px-2 py-1 text-xs rounded', secondaryButtonClass)}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                ) : (
                                  <>
                                    <button
                                      onClick={() => startEditingReminder(reminder)}
                                      className={cn('px-2 py-1 text-xs rounded', secondaryButtonClass)}
                                    >
                                      Reschedule
                                    </button>
                                    <button
                                      onClick={() => handleDismissReminder(reminder.id)}
                                      className={cn(
                                        'px-2 py-1 text-xs rounded',
                                        isEdgeMail ? 'text-[var(--em-status-danger-text)] bg-[var(--em-status-danger-bg)]' : 'text-red-600 bg-red-50 dark:bg-red-900/20',
                                      )}
                                    >
                                      Dismiss
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'subtasks' && (
                  <div className="space-y-4">
                    {/* Progress bar */}
                    {subtaskProgress.total > 0 && (
                      <div>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className={textTertiary}>Progress</span>
                          <span className={cn('font-medium', textPrimary)}>
                            {subtaskProgress.completed} of {subtaskProgress.total} complete
                          </span>
                        </div>
                        <div className={cn('w-full rounded-full h-2', isEdgeMail ? 'bg-[var(--em-border-secondary)]' : 'bg-gray-200 dark:bg-gray-700')}>
                          <div
                            className={cn('h-2 rounded-full transition-all', isEdgeMail ? 'bg-[var(--em-accent-primary)]' : 'bg-lex-accent')}
                            style={{ width: `${subtaskProgress.total > 0 ? (subtaskProgress.completed / subtaskProgress.total) * 100 : 0}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Add subtask button / form */}
                    {showAddSubtask ? (
                      <div
                        className={cn(
                          'rounded-lg p-3 border space-y-2',
                          isEdgeMail ? 'bg-[var(--em-surface-toolbar)] border-[var(--em-border-secondary)]' : 'bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700',
                        )}
                      >
                        <input
                          type="text"
                          value={newSubtask.title || ''}
                          onChange={(e) => setNewSubtask(p => ({ ...p, title: e.target.value }))}
                          placeholder="Subtask title..."
                          className={cn('w-full px-3 py-2 rounded-lg border text-sm', inputClass)}
                          autoFocus
                          onKeyDown={(e) => { if (e.key === 'Enter') handleCreateSubtask(); }}
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <select
                            value={newSubtask.assignee_id || ''}
                            onChange={(e) => setNewSubtask(p => ({ ...p, assignee_id: e.target.value || undefined }))}
                            className={cn('px-2 py-1.5 rounded-lg border text-sm', selectClass)}
                          >
                            <option value="">Assign to me</option>
                            {attorneys.map(u => (
                              <option key={u.id} value={u.keycloakUserId || u.id}>{u.name}</option>
                            ))}
                          </select>
                          <input
                            type="date"
                            value={newSubtask.due_date || ''}
                            onChange={(e) => setNewSubtask(p => ({ ...p, due_date: e.target.value }))}
                            className={cn('px-2 py-1.5 rounded-lg border text-sm', inputClass)}
                          />
                        </div>
                        {/* Notify assignee checkbox - only show when an assignee is selected */}
                        {newSubtask.assignee_id && (
                          <label className={cn('flex items-center gap-2 text-sm cursor-pointer', textSecondary)}>
                            <input
                              type="checkbox"
                              checked={notifyOnCreate}
                              onChange={(e) => setNotifyOnCreate(e.target.checked)}
                              className={cn(
                                'rounded',
                                isEdgeMail
                                  ? 'border-[var(--em-border-secondary)] text-[var(--em-accent-primary)] focus:ring-[var(--em-state-focus)]'
                                  : 'border-gray-300 dark:border-gray-600 text-lex-accent focus:ring-lex-accent',
                              )}
                            />
                            Notify assignee via email
                          </label>
                        )}
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => { setShowAddSubtask(false); setNotifyOnCreate(false); }}
                            className={cn('px-3 py-1 text-xs rounded-lg', secondaryButtonClass)}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleCreateSubtask}
                            disabled={submittingSubtask || !newSubtask.title?.trim()}
                            className={cn('px-3 py-1 text-xs rounded-lg disabled:opacity-50', primaryButtonClass)}
                          >
                            {submittingSubtask ? 'Adding...' : 'Add'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => setShowAddSubtask(true)}
                        className={cn(
                          'w-full py-2 text-sm border border-dashed rounded-lg transition-colors',
                          isEdgeMail
                            ? 'text-[var(--em-accent-primary)] border-[var(--em-border-secondary)] hover:bg-[var(--em-state-hover)]'
                            : 'text-lex-accent border-gray-300 dark:border-gray-600 hover:bg-lex-state-hover',
                        )}
                      >
                        + Add Subtask
                      </button>
                    )}

                    {/* Subtask list */}
                    {subtasks.length === 0 ? (
                      <div className={cn('text-center py-6', textTertiary)}>No subtasks yet.</div>
                    ) : (
                      <div className="space-y-1">
                        {subtasks.map((st) => (
                          <div key={st.id}>
                            {editingSubtaskId === st.id ? (
                              /* Inline edit form */
                              <div
                                className={cn(
                                  'rounded-lg border p-3 space-y-2',
                                  isEdgeMail
                                    ? 'border-[var(--em-accent-primary)] bg-[var(--em-accent-primary-light)]'
                                    : 'border-lex-accent bg-lex-accent-soft',
                                )}
                              >
                                <input
                                  type="text"
                                  value={editSubtaskForm.title}
                                  onChange={(e) => setEditSubtaskForm(p => ({ ...p, title: e.target.value }))}
                                  className={cn('w-full px-3 py-1.5 rounded-lg border text-sm', inputClass)}
                                  autoFocus
                                  onKeyDown={(e) => { if (e.key === 'Enter') handleSaveSubtask(); if (e.key === 'Escape') setEditingSubtaskId(null); }}
                                />
                                <div className="grid grid-cols-2 gap-2">
                                  <select
                                    value={editSubtaskForm.assignee_id}
                                    onChange={(e) => setEditSubtaskForm(p => ({ ...p, assignee_id: e.target.value }))}
                                    className={cn('px-2 py-1.5 rounded-lg border text-sm', selectClass)}
                                  >
                                    <option value="">Assign to me</option>
                                    {attorneys.map(u => (
                                      <option key={u.id} value={u.keycloakUserId || u.id}>{u.name}</option>
                                    ))}
                                  </select>
                                  <input
                                    type="date"
                                    value={editSubtaskForm.due_date}
                                    onChange={(e) => setEditSubtaskForm(p => ({ ...p, due_date: e.target.value }))}
                                    className={cn('px-2 py-1.5 rounded-lg border text-sm', inputClass)}
                                  />
                                </div>
                                <div className="flex gap-2 justify-end">
                                  <button
                                    onClick={() => setEditingSubtaskId(null)}
                                    className={cn('px-3 py-1 text-xs rounded-lg', secondaryButtonClass)}
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    onClick={handleSaveSubtask}
                                    disabled={savingSubtask || !editSubtaskForm.title.trim()}
                                    className={cn('px-3 py-1 text-xs rounded-lg disabled:opacity-50', primaryButtonClass)}
                                  >
                                    {savingSubtask ? 'Saving...' : 'Save'}
                                  </button>
                                </div>
                              </div>
                            ) : (
                              /* Display mode */
                              <div
                                className={cn(
                                  'flex items-center gap-3 px-3 py-2 rounded-lg border transition-colors',
                                  isEdgeMail ? 'border-[var(--em-border-secondary)]' : 'border-gray-200 dark:border-gray-700',
                                  st.status === 'done'
                                    ? isEdgeMail
                                      ? 'bg-[var(--em-surface-toolbar)]'
                                      : 'bg-gray-50 dark:bg-gray-900/50'
                                    : isEdgeMail
                                      ? 'hover:bg-[var(--em-state-hover)]'
                                      : 'hover:bg-gray-50 dark:hover:bg-gray-800',
                                )}
                              >
                                {/* Checkbox */}
                                <button
                                  onClick={() => handleSubtaskStatusToggle(st)}
                                  className={cn(
                                    'w-5 h-5 rounded border flex-shrink-0 flex items-center justify-center transition-colors',
                                    st.status === 'done'
                                      ? isEdgeMail
                                        ? 'bg-[var(--em-accent-primary)] border-[var(--em-accent-primary)] text-white'
                                        : 'bg-lex-accent border-lex-accent text-lex-accent-contrast'
                                      : isEdgeMail
                                        ? 'border-[var(--em-border-secondary)] hover:border-[var(--em-accent-primary)]'
                                        : 'border-gray-300 dark:border-gray-600 hover:border-lex-accent',
                                  )}
                                >
                                  {st.status === 'done' && (
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                    </svg>
                                  )}
                                </button>

                                {/* Content */}
                                <div className="flex-1 min-w-0">
                                  <div className={cn('text-sm', st.status === 'done' ? `line-through ${textMuted}` : textPrimary)}>
                                    {st.title}
                                  </div>
                                  <div className={cn('flex gap-3 items-center text-xs', textTertiary)}>
                                    {st.assignee && (
                                      <span>{displayName(st.assignee)}</span>
                                    )}
                                    {st.assignment_status && (
                                      <span
                                        className={cn(
                                          'px-1.5 py-0.5 rounded-full text-[10px] font-medium',
                                          isEdgeMail
                                            ? st.assignment_status === 'accepted'
                                              ? 'bg-[var(--em-status-success-bg)] text-[var(--em-status-success-text)]'
                                              : st.assignment_status === 'rejected'
                                                ? 'bg-[var(--em-status-danger-bg)] text-[var(--em-status-danger-text)]'
                                                : 'bg-[var(--em-status-warning-bg)] text-[var(--em-status-warning-text)]'
                                            : st.assignment_status === 'accepted'
                                              ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300'
                                              : st.assignment_status === 'rejected'
                                                ? 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300'
                                                : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-300',
                                        )}
                                      >
                                        {st.assignment_status === 'accepted' ? 'Accepted' : st.assignment_status === 'rejected' ? 'Declined' : 'Pending'}
                                      </span>
                                    )}
                                    {st.due_date && (
                                      <span
                                        className={
                                          new Date(st.due_date) < new Date() && st.status !== 'done'
                                            ? (isEdgeMail ? 'text-[var(--em-status-danger-text)]' : 'text-red-500')
                                            : ''
                                        }
                                      >
                                        Due: {new Date(st.due_date).toLocaleDateString()}
                                      </span>
                                    )}
                                  </div>
                                </div>

                                {/* Actions */}
                                <div className="flex gap-1 flex-shrink-0">
                                  <button
                                    onClick={() => startEditingSubtask(st)}
                                    title="Edit subtask"
                                    className={cn(
                                      'px-1.5 py-0.5 text-xs',
                                      isEdgeMail ? 'text-[var(--em-text-tertiary)] hover:text-[var(--em-accent-primary)]' : 'text-gray-500 hover:text-lex-accent',
                                    )}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => openSubtaskModal(st.id)}
                                    title="Open subtask"
                                    className={cn(
                                      'px-1.5 py-0.5 text-xs',
                                      isEdgeMail ? 'text-[var(--em-text-tertiary)] hover:text-[var(--em-accent-primary)]' : 'text-gray-500 hover:text-lex-accent',
                                    )}
                                  >
                                    Open
                                  </button>
                                  <button
                                    onClick={() => openSubtaskModal(st.id, 'reminders')}
                                    title="Open subtask reminders"
                                    className={cn(
                                      'px-1.5 py-0.5 text-xs',
                                      isEdgeMail ? 'text-[var(--em-text-tertiary)] hover:text-[var(--em-accent-primary)]' : 'text-gray-500 hover:text-lex-accent',
                                    )}
                                  >
                                    Reminders
                                  </button>
                                  <button
                                    onClick={() => handlePromoteSubtask(st.id)}
                                    title="Promote to task"
                                    className={cn(
                                      'px-1.5 py-0.5 text-xs',
                                      isEdgeMail ? 'text-[var(--em-text-tertiary)] hover:text-[var(--em-accent-primary)]' : 'text-gray-500 hover:text-lex-accent',
                                    )}
                                  >
                                    Promote
                                  </button>
                                  <button
                                    onClick={() => handleDeleteSubtask(st.id)}
                                    title="Delete subtask"
                                    className={cn(
                                      'px-1.5 py-0.5 text-xs',
                                      isEdgeMail
                                        ? 'text-[var(--em-text-tertiary)] hover:text-[var(--em-status-danger-text)]'
                                        : 'text-gray-500 hover:text-red-600 dark:hover:text-red-400',
                                    )}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'comments' && (
                  <div className="space-y-4">
                    {/* Add comment */}
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleAddComment(); }}
                        placeholder="Add a comment..."
                        className={cn('flex-1 px-3 py-2 rounded-lg border', inputClass)}
                      />
                      <button
                        onClick={handleAddComment}
                        disabled={!newComment.trim()}
                        className={cn('px-4 py-2 rounded-lg disabled:opacity-50', primaryButtonClass)}
                      >
                        Post
                      </button>
                    </div>

                    {comments.length === 0 ? (
                      <div className={cn('text-center py-8', textTertiary)}>No comments yet.</div>
                    ) : (
                      comments.map(comment => (
                        <div
                          key={comment.id}
                          className={cn(
                            'border rounded-lg p-3',
                            isEdgeMail ? 'border-[var(--em-border-secondary)] bg-[var(--em-surface-card)]' : 'border-gray-200 dark:border-gray-700',
                          )}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <span className={cn('text-sm font-medium', textPrimary)}>
                              {displayName(comment.author)}
                            </span>
                            {comment.is_system && (
                              <span
                                className={cn(
                                  'px-1.5 py-0.5 text-xs rounded',
                                  isEdgeMail ? 'bg-[var(--em-surface-toolbar)] text-[var(--em-text-secondary)]' : 'bg-gray-200 dark:bg-gray-700',
                                )}
                              >
                                System
                              </span>
                            )}
                            <span className={cn('text-xs', textTertiary)}>{formatDateTime(comment.created_at)}</span>
                          </div>
                          <p className={cn('text-sm whitespace-pre-wrap', textSecondary)}>{comment.content}</p>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {activeTab === 'activity' && (
                  <div className="space-y-0">
                    {auditLog.length === 0 ? (
                      <div className={cn('text-center py-8', textTertiary)}>No activity logged yet.</div>
                    ) : (
                      <div className="relative">
                        {/* Timeline line */}
                        <div
                          className={cn(
                            'absolute left-4 top-6 bottom-6 w-0.5',
                            isEdgeMail ? 'bg-[var(--em-border-secondary)]' : 'bg-gray-200 dark:bg-gray-700',
                          )}
                        />

                        {auditLog.map((log, idx) => {
                          const cfg = activityConfig[log.action] || defaultActivity;
                          const Icon = cfg.icon;
                          return (
                            <div key={log.id} className="relative flex items-start gap-3 py-3 pl-0">
                              {/* Icon dot */}
                              <div className={`relative z-10 flex items-center justify-center w-8 h-8 rounded-full ${cfg.bg} shrink-0`}>
                                <Icon className={`w-4 h-4 ${cfg.color}`} />
                              </div>
                              {/* Content */}
                              <div className="flex-1 min-w-0 pt-0.5">
                                <p className={cn('text-sm', textSecondary)}>
                                  <span className={cn('font-medium', textPrimary)}>{displayName(log.user)}</span>
                                  {' '}
                                  {getActivityDescription(log)}
                                </p>
                                <p className={cn('text-xs mt-0.5', textMuted)} title={formatDateTime(log.created_at)}>
                                  {formatRelativeTime(log.created_at)}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {subtaskModalTaskId && (
        <TaskDetailModal
          isOpen={true}
          taskId={subtaskModalTaskId}
          initialTab={subtaskModalInitialTab}
          onClose={() => setSubtaskModalTaskId(null)}
          onUpdated={() => fetchSubtasks()}
          theme={theme}
        />
      )}
    </div>
  );
};

export default TaskDetailModal;
